import { z } from 'zod';

/**
 * Archived notifications are removed for good after this many days. A Tenant Admin sets it in
 * the app (GET/PUT /api/v1/admin/notification-settings); until then the school server's
 * TP_NOTIFICATION_ARCHIVE_RETENTION_DAYS (default 90) applies. Notifications still in the inbox
 * are never removed automatically.
 */
export const NOTIFICATION_ARCHIVE_RETENTION_DAYS_DEFAULT = 90;
export const NOTIFICATION_ARCHIVE_RETENTION_DAYS_MAX = 365;

/** Quick choices on the admin Notifications screen (any whole day count in range is allowed). */
export const NOTIFICATION_RETENTION_PRESETS = [7, 30, 90, 180, 365] as const;

export const archiveRetentionDaysSchema = z
  .number()
  .int()
  .min(1)
  .max(NOTIFICATION_ARCHIVE_RETENTION_DAYS_MAX);

/** GET/PUT /api/v1/admin/notification-settings (notifications.manage, tenant-admin-only). */
export const notificationSettingsSchema = z.object({
  archiveRetentionDays: archiveRetentionDaysSchema,
});
export type NotificationSettings = z.infer<typeof notificationSettingsSchema>;

/** GET /api/v1/notifications — the user's inbox (Phase 1: test pushes only). */
export const notificationSchema = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  title: z.string().min(1),
  body: z.string(),
  refId: z.string().nullable(),
  createdAt: z.iso.datetime(),
  readAt: z.iso.datetime().nullable(),
  /**
   * When the user archived it (per user, on the school server); null in the inbox. Optional on
   * the wire so an app talking to an older server (no archive yet) still parses (apiVersion 1).
   */
  archivedAt: z.iso.datetime().nullable().optional(),
});
export type Notification = z.infer<typeof notificationSchema>;

/** `?view=` on GET /api/v1/notifications: the inbox (default) or the user's archive. */
export const NOTIFICATION_VIEWS = ['inbox', 'archived'] as const;
export const notificationViewSchema = z.enum(NOTIFICATION_VIEWS);
export type NotificationView = z.infer<typeof notificationViewSchema>;

export const notificationListSchema = z.object({
  items: z.array(notificationSchema),
  nextCursor: z.string().nullable(),
  /**
   * Unread, non-archived notifications across every page (the tab badge). Optional: older
   * servers don't send it, and the app then counts the first page.
   */
  unreadCount: z.number().int().min(0).optional(),
  /**
   * `?view=archived` only: archived items are deleted after this many days (the school's
   * effective setting). Optional: older servers and the inbox view don't send it.
   */
  archiveRetentionDays: archiveRetentionDaysSchema.optional(),
});
export type NotificationList = z.infer<typeof notificationListSchema>;

/** POST /api/v1/notifications/read-all — how many unread items were marked read. */
export const notificationsReadAllResultSchema = z.object({
  updated: z.number().int().min(0),
});
export type NotificationsReadAllResult = z.infer<typeof notificationsReadAllResultSchema>;

/**
 * POST /api/v1/notifications/archive-read — archives every read, non-archived notification of
 * the caller. `ids` lets the app offer Undo (unarchive each).
 */
export const notificationsArchiveReadResultSchema = z.object({
  updated: z.number().int().min(0),
  ids: z.array(z.string().min(1)),
});
export type NotificationsArchiveReadResult = z.infer<typeof notificationsArchiveReadResultSchema>;
